import React, { useCallback, useEffect, useState } from "react";
import { Button, InputNumber, Spin, Switch, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import * as api from "../controller";
import OfferEditorModal from "./OfferEditorModal";
import OfferList from "./OfferList";
import SalesTaxCard from "./SalesTaxCard";
import { chargeView, planLine, planStatePill, planStateSentence, shortDate } from "./planState";
import "../leadSettings.css";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

export function BillingSection({ icon, title, sub, extra, children }) {
  return (
    <div className="crm-section">
      <div className="crm-section-head">
        {icon ? <span className="crm-section-icon">{icon}</span> : null}
        <span className="crm-section-title">{title}</span>
        {sub ? <span className="crm-section-sub">{sub}</span> : null}
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>{extra}</span>
      </div>
      <div className="crm-section-body">{children}</div>
    </div>
  );
}

export function PlanStatePill({ row }) {
  const pill = planStatePill(row);
  return <span className={`ub-state ${pill.cls}`}>{pill.label}</span>;
}

/**
 * CRM office drawer → Users & Billing (below the users): the office's plan, the
 * complimentary-access switch, its sales tax, the free-trial cap and its special
 * offers — its own and those it inherits from its practice. Super admins only;
 * nothing here charges anyone.
 *
 * `onChanged` refreshes the drawer's hero chips after a change.
 */
export default function OfficeBillingPanel({ officeId, onChanged }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editor, setEditor] = useState(null); // {offer?} while the editor is open

  const load = useCallback(async () => {
    const r = await api.getOfficeBilling(officeId);
    setFailed(!ok(r));
    if (ok(r)) setData(r.json);
  }, [officeId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const changed = () => { load(); if (onChanged) onChanged(); };

  if (failed && !data) {
    return (
      <p className="crm-sub" style={{ margin: "16px 24px" }}>
        Couldn&rsquo;t load this office&rsquo;s billing.{" "}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
      </p>
    );
  }
  if (!data) return <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>;

  const own = (data.offers || []).filter((o) => !o.inherited);
  const inherited = (data.offers || []).filter((o) => o.inherited);

  return (
    <>
      <BillingSection title="Plan" extra={<PlanStatePill row={data} />}>
        <PlanFacts plan={data.plan} />
        {planStateSentence(data) ? (
          <div className={`ub-callout${data.state === "blocked" ? " is-warn" : ""}`}>{planStateSentence(data)}</div>
        ) : null}
        <div className="leadset-panel" style={{ borderTop: "none" }}>
          <ComplimentarySwitch
            officeId={officeId}
            value={!!data.complimentary_access}
            subscribed={data.state === "subscribed"}
            onChanged={changed}
          />
        </div>
      </BillingSection>

      <BillingSection title="Sales tax">
        <SalesTaxCard
          officeId={officeId}
          summary={data.sales_tax}
          onChanged={(salesTax) => setData((d) => ({ ...d, sales_tax: salesTax }))}
        />
      </BillingSection>

      <BillingSection title="Free trial">
        <TrialQuotaEditor officeId={officeId} trial={data.trial_detail} onChanged={changed} />
      </BillingSection>

      <BillingSection
        title="Special offers"
        extra={(
          <Button size="small" type="primary" ghost icon={<PlusOutlined />} onClick={() => setEditor({})}>
            New offer
          </Button>
        )}
      >
        <OfferList
          offers={[...own, ...inherited]}
          officeNames={{ [data.office_id]: data.office_name }}
          practiceName={data.practice_name}
          onEdit={(offer) => setEditor({ offer })}
          onChanged={load}
          emptyText={
            data.practice_name
              ? `No special offers. Offers put on ${data.practice_name} apply here too.`
              : "No special offers."
          }
        />
      </BillingSection>

      <OfferEditorModal
        open={!!editor}
        offer={editor && editor.offer}
        scope={{ officeId: data.office_id, name: data.office_name }}
        onClose={() => setEditor(null)}
        onSaved={load}
      />
    </>
  );
}

export function PlanFacts({ plan }) {
  if (!plan) return null;
  const charge = chargeView(plan.charge);
  return (
    <div className="ub-facts">
      <Fact label="Plan" value={planLine(plan)} />
      <Fact label="Bought by" value={plan.bought_by || "—"} />
      <Fact label={plan.status === "pending" ? "First charge" : "Renews"} value={shortDate(plan.next_billing)} />
      {charge ? (
        <Fact
          label="Each charge (incl. tax)"
          value={<>{charge.list ? <span className="ub-strike">{charge.list}</span> : null}{charge.total}</>}
          note={charge.note}
        />
      ) : null}
      {charge ? <Fact label="Tax in each charge" value={charge.tax || "No sales tax"} /> : null}
    </div>
  );
}

function Fact({ label, value, note }) {
  return (
    <div>
      <div className="ub-fact-label">{label}</div>
      <div className="ub-fact-value">{value}</div>
      {note ? <div className="ub-fact-note">{note}</div> : null}
    </div>
  );
}

/** Complimentary access: the office uses Smilepass with no plan and no trial. */
function ComplimentarySwitch({ officeId, value, subscribed, onChanged }) {
  const [saving, setSaving] = useState(false);
  const change = async (next) => {
    setSaving(true);
    const r = await api.updateOfficeUsageSettings(officeId, { complimentary_access: next });
    setSaving(false);
    if (ok(r)) {
      message.success(next ? "Complimentary access is on." : "Complimentary access is off.");
      onChanged();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't change complimentary access.");
    }
  };
  return (
    <div className="leadset-row">
      <div className="leadset-row-label">
        Complimentary access
        <div className="leadset-hint">
          {subscribed
            ? "They have a paid plan, so this only matters if the plan ends. It never stops a charge."
            : "Use Smilepass without a plan or a trial, never charged — for partners, pilots and our own offices."}
        </div>
      </div>
      <div className="leadset-row-control">
        <Switch checked={value} loading={saving} onChange={change} />
      </div>
    </div>
  );
}

/**
 * Free-trial cap for one office. Usage is counted live, so this one number both
 * extends and ends a trial: set it above what they've already used and the trial
 * resumes on their next request; at or below and it's over. Blank reverts to the
 * global default. Setting a cap on an office that never started a trial grants
 * one. (Moved here from the drawer's header.)
 */
export function TrialQuotaEditor({ officeId, trial, onChanged }) {
  const [value, setValue] = useState(trial && trial.quota_override != null ? trial.quota_override : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(trial && trial.quota_override != null ? trial.quota_override : null);
  }, [trial && trial.quota_override]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!trial) return null;
  const used = trial.used || 0;
  const quota = trial.quota || 0;
  const started = !!(trial.active || trial.exhausted);

  const save = async (next) => {
    setSaving(true);
    try {
      const res = await api.setOfficeTrialQuota(officeId, next);
      if (ok(res)) {
        const q = res.json || {};
        message.success(
          q.exhausted
            ? `Trial ended — ${q.used} of ${q.quota} used.`
            : `Trial active — ${q.remaining} request${q.remaining === 1 ? "" : "s"} left.`,
        );
        if (onChanged) onChanged();
      } else {
        message.error((res && res.json && res.json.detail) || "Couldn't update the trial cap.");
      }
    } catch (e) {
      message.error("Couldn't update the trial cap.");
    } finally {
      setSaving(false);
    }
  };

  let status;
  if (trial.subscribed) status = <>Not in use — they have a paid plan.</>;
  else if (!started) status = <>Not started. Setting a cap grants them a trial now.</>;
  else {
    status = (
      <>
        <b>{used}</b> of <b>{quota}</b> requests used
        {trial.exhausted ? " · ended" : ` · ${trial.remaining} left`}
      </>
    );
  }

  return (
    <div className="leadset-panel" style={{ borderTop: "none" }}>
      <div className="leadset-row">
        <div className="leadset-row-label">
          {status}
          <div className="leadset-hint">
            {trial.quota_override != null ? `Custom cap of ${trial.quota_override}` : `Global default of ${quota}`}
            {started ? ` · above ${used} resumes the trial; ${used} or below ends it now.` : ""}
          </div>
        </div>
        <div className="leadset-row-control">
          <InputNumber
            min={0}
            value={value}
            placeholder={`default (${quota})`}
            onChange={setValue}
            style={{ width: 140 }}
          />
          <Button size="small" type="primary" loading={saving} onClick={() => save(value)}>
            {started ? "Set cap" : "Grant trial"}
          </Button>
          {trial.quota_override != null ? (
            <Button size="small" loading={saving} onClick={() => save(null)}>Use default</Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
